<script setup>
import { computed, inject, ref, watch, watchEffect, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useBreakpoints, useStorage } from '@vueuse/core'
import AppButton from '@/components/atoms/AppButton'
import AppInput from '@/components/atoms/AppInput'
import AppSkeleton from '@/components/atoms/AppSkeleton'
import { AppDropdown, AppDropdownItem } from '@/components/molecules/AppDropdown'
import AppTabToggle from '@/components/molecules/AppTabToggle'
import AppSidePanel from '@/components/organisms/AppSidePanel'
import HelpHubGetStarted from './HelpHubGetStarted.vue'
import HelpHubGetStartedList from './HelpHubGetStartedList.vue'
import HelpHubState from './HelpHubState.vue'
import HelpHubStepPage from './HelpHubStepPage.vue'
import HelpHubWhatsNew from './HelpHubWhatsNew.vue'
import HelpHubExploreTopics from './HelpHubExploreTopics.vue'
import HelpHubHelpCategories from './HelpHubHelpCategories.vue'
import HelpHubSearchResults from './HelpHubSearchResults.vue'
import HelpHubChat from './Chat/HelpHubChat.vue'
import HelpHubChatComposer from './Chat/HelpHubChatComposer.vue'
import HelpHubChatList from './Chat/HelpHubChatList.vue'
import { useGlobalStore } from '@/stores/GlobalStore'
import { useStrapiStore } from '@/stores/StrapiStore'
import { useSupportStore } from '@/stores/SupportStore'
import { useDevice } from '@/hooks/useDevice'
import { useHelpHub } from '@/hooks/useHelpHub'
import { useGetStarted } from '@/hooks/useGetStarted'
import { arrivedAt, helpOf, helpOfRoute, isChatSlug, isTicketSlug, pagesIn } from '@/helpers/helpHub'
import { ticketDigits } from '@/helpers/support'
import { safeCapture } from '@/helpers/posthog'
import {
  POSTHOG_EVENTS,
  HELP_BAR_SOURCE,
  HELP_PAGE_SOURCE,
  BREAKPOINTS,
  CDP_SUPPORT_URL,
  HELP_HUB_WIDTH,
  HELP_HUB_MAX_WIDTH,
  HELP_HUB_WIDTH_KEY,
  HELP_HUB_PANEL_ID,
  HELP_HUB_TOGGLE_ID,
  HELP_HUB_OPEN_KEY,
  HELP_HUB_GET_STARTED_SLUG,
  HELP_HUB_TAB,
  HELP_HUB_TABS,
  SUPPORT_ESCALATE_LABEL,
} from '@/constants'

const globalStore = useGlobalStore()
const strapiStore = useStrapiStore()
const supportStore = useSupportStore()
const route = useRoute()
const router = useRouter()
const makeRoomForHelpHub = inject('makeRoomForHelpHub')
const { isMobile } = useDevice()
const { helpSlug, helpTab, helpPage, chatPage, ticketNumber, helpLink, openHelp, openTab, openTicket, closeHelp } = useHelpHub()
const { steps } = useGetStarted()
// Mirrors AppSidePanel's `max-2xl:` switch: a column from 2xl up, an overlay below.
const isColumn = useBreakpoints(BREAKPOINTS).greaterOrEqual('2xl')

// Follows the URL, but switches at once on a click: the navigation can wait on a route guard.
const tab = ref(helpTab.value)
const search = ref('')
// What the dealer is typing to support, kept here because the composer sits in the panel's footer.
const draft = ref('')
// Help pages opened in the hub, oldest first.
const trail = ref([])
const panel = ref(null)
const savedWidth = useStorage(HELP_HUB_WIDTH_KEY, HELP_HUB_WIDTH)

const panelWidth = computed({
  get: () => isMobile.value ? HELP_HUB_WIDTH : Math.min(HELP_HUB_MAX_WIDTH, Math.max(
    HELP_HUB_WIDTH, Number.isFinite(savedWidth.value) ? savedWidth.value : HELP_HUB_WIDTH,
  )),
  set: (width) => { savedWidth.value = width },
})

// Only the in-flow column takes width; the overlay covers the page instead.
const pushed = computed(() => globalStore.helpHubOpen && isColumn.value)
// A detail panel floats over the page and takes Escape first.
const closeOnEscape = computed(() => !globalStore.detailPanelOpen)
const isHelpTab = computed(() => tab.value === HELP_HUB_TAB.HELP)
// Words in the field: the Help tab shows what they match in place of its sections.
const searching = computed(() => search.value.trim() !== '')
const isGetStartedList = computed(() => helpPage.value === HELP_HUB_GET_STARTED_SLUG)
// Where the arrow leads: the page before this one on the trail, or the hub's top level.
const previousPage = computed(() => trail.value.at(-2) ?? null)
const loading = computed(() => strapiStore.getStartedLoading)
const failed = computed(() => strapiStore.getStartedFailed)
// The list needs the steps; while they load it draws its own skeleton.
const showsList = computed(() => isGetStartedList.value && (steps.value.length > 0 || loading.value))
// The items that lead down from `holder` to the page `slug`, each one's page holding the next; null when it is not below `holder`.
const guidePathIn = (holder, slug) => {
  for (const item of pagesIn(holder)) {
    if (!item.page) continue
    if (item.slug === slug) return [item]

    const below = guidePathIn(item.page, slug)
    if (below) return [item, ...below]
  }

  return null
}
// The Getting Started step that is open, or that holds the open page; a locked step's page opens too.
const stepIndex = computed(() => steps.value.findIndex((step) => (step.key === helpPage.value && (step.guide || step.guides)) || guidePathIn(step, helpPage.value)))
const step = computed(() => steps.value[stepIndex.value])
// Strapi answered without this page.
const missing = computed(() => !!helpPage.value && !showsList.value && !step.value && !loading.value && !failed.value)
// The top level's frame, with the tabs and the field: the two states take the content's place in it (Figma 29796:69281 / 29796:69282); the Chat tab's list pages have a page's frame instead.
const chrome = computed(() => !chatPage.value && (!helpPage.value || failed.value || missing.value))
// A help page swaps the title for the way back up.
const pageHeader = computed(() => !chrome.value)
// From `step` down to the open guide step; empty on the step's own page.
const guidePath = computed(() => (step.value && guidePathIn(step.value, helpPage.value)) || [])
// The open guide step's page; a title or an intro it lacks comes from its guide step.
const guidePage = computed(() => {
  const item = guidePath.value.at(-1)
  return item && { title: item.title, helpDescription: item.linkDescription ?? item.description, ...item.page }
})
// The locked steps are a track of their own until a sale unlocks them: the step before them has none to jump to.
const nextStep = computed(() => {
  const next = step.value && steps.value[stepIndex.value + 1]
  return next && (step.value.locked || !next.locked) ? next : null
})
// What a page is listed as on the "Back to" menu: a step's help title, or the title of the guide step that opened the page (shorter than the page's own heading); null for a page the steps don't name.
const titleOf = (slug) => {
  for (const item of steps.value) {
    if (item.key === slug) return item.helpTitle ?? item.title
    const guideStep = guidePathIn(item, slug)?.at(-1)
    if (guideStep) return guideStep.title
  }

  return null
}
// The pages opened on the way to this one, the open page last; the top level and the list head every path and have rows of their own, and a page the steps don't name yet has none.
const trailPages = computed(() => trail.value
  .filter((slug) => slug !== HELP_HUB_GET_STARTED_SLUG)
  .map((slug) => ({ slug, title: titleOf(slug) }))
  .filter((page) => page.title))
// A trail row sits under the icons, joined to the rows above by a line through the icons' centre that runs from the previous row's text to its own; blue beside the open page.
const TRAIL_ROW_CLASS = 'relative !pl-[50px] !pr-6 before:absolute before:-top-2 before:bottom-2 before:left-7 before:w-[1.5px]'
const trailRowClass = (page) => `${TRAIL_ROW_CLASS} ${page.slug === helpPage.value ? 'before:bg-tint-1 font-medium text-tint-1' : 'before:bg-gray-200'}`
// "What's next?" on a guide step's page, for a step that has it: links to the step's other guide pages; a `link*` field is the card's own.
const nextLinks = computed(() => guidePage.value && step.value.whatsNext && step.value.guide
  .filter((item) => item.slug !== helpPage.value && (item.page || item.linkTitle))
  .map((item) => ({
    slug: item.slug,
    title: item.linkTitle ?? item.page.title,
    description: item.linkDescription ?? item.description,
    icon: item.linkIcon,
  })))

// Open until the dealer closes it once; a URL that names a place in the hub opens it too. On a phone the hub covers the page, so only the URL opens it.
const startsOpen = !!helpSlug.value || (!isMobile.value && localStorage.getItem(HELP_HUB_OPEN_KEY) !== 'false')
// Arriving with it open (a first load, or straight after signing in) shows the route's page, as entering the route in the app does; it opens once the URL names that page, so the main page doesn't flash first.
const arrivalPage = startsOpen && !helpSlug.value ? helpOfRoute(route) : null
if (arrivalPage) openHelp(arrivalPage).finally(() => { globalStore.helpHubOpen = true })
else globalStore.helpHubOpen = startsOpen

// Focus returns to the toggle when it was in the hub or nowhere; an Escape from a page control leaves that control alone.
const close = () => {
  const active = document.activeElement
  const fromHub = !active || active === document.body || !!document.getElementById(HELP_HUB_PANEL_ID)?.contains(active)
  globalStore.setHelpHubOpen(false)
  if (fromHub) document.getElementById(HELP_HUB_TOGGLE_ID)?.focus()
}

// Clear the field on the way out, so the sent line isn't still sitting there under the reply.
const sendMessage = () => {
  const content = draft.value
  // Files alone are a message too; the store decides whether there is anything to send.
  if (!content.trim() && !supportStore.readyAttachmentIds.length) return
  draft.value = ''
  supportStore.send(content)
}

// The open thread's ticket number, off its conversation record; '' while it has none (a new thread until the list refetches, the local "What's new?" always).
const threadTicket = computed(() => ticketDigits(supportStore.activeConversation))

// Back out of the thread: the store drops it, and a URL that named the ticket returns to the Chat landing.
const closeThread = () => {
  if (supportStore.inConversation) supportStore.closeConversation()
  if (ticketNumber.value) openHelp(HELP_HUB_TAB.CHAT)
}

// The URL names a thread (`?help=ticket-32193`): the conversation with that number opens once the list has loaded, and stays open on a repeat; a number the list lacks opens nothing, and the Chat tab says so.
watch([ticketNumber, () => supportStore.conversationsLoading, () => supportStore.conversations], ([number, loading]) => {
  if (!number || loading) return
  const conversation = supportStore.conversations.find((item) => ticketDigits(item) === number)
  if (!conversation || conversation.id === supportStore.activeId) return
  if (supportStore.activeId) supportStore.closeConversation()
  supportStore.openConversation(conversation.id)
}, { immediate: true })

// Leaving the ticket's slug for the Chat landing or a list page ends the thread; any other move (the Help tab, closing the hub) keeps it for the way back.
watch(helpSlug, (slug) => {
  if (isChatSlug(slug) && !isTicketSlug(slug) && threadTicket.value) supportStore.closeConversation()
})

// A thread opened before it had a number (a new conversation, until the list refetches) takes the slug once it has one, so a reload and the hub's memory return to it.
watch(threadTicket, (number) => {
  if (number && isChatSlug(helpSlug.value) && !isTicketSlug(helpSlug.value)) openTicket(number)
})

// Whatever the navigation's outcome, the tab ends on what the URL says; Chat lands on the open thread, which the URL names by its ticket.
const selectTab = async (value) => {
  tab.value = value
  const thread = value === HELP_HUB_TAB.CHAT ? threadTicket.value : ''
  await (thread ? openTicket(thread) : openTab(value))
  tab.value = helpTab.value
}

// Fixed bottom bars subtract this so they stop at the column's edge.
watchEffect(() => {
  globalStore.helpHubWidth = panelWidth.value
  document.documentElement.style.setProperty('--help-hub-inset', pushed.value ? `${panelWidth.value}px` : '0px')
})

// Two facts about the navigation under way: Back and Forward land on a URL the browser has already changed to; a link into the hub names a page the URL did not have, where the guard's own carry arrives as a redirect.
let fromHistory = false
let linkNamedPage = false
const stopWatchingNavigation = router.beforeEach((to, from) => {
  fromHistory = arrivedAt(router, to)
  linkNamedPage = !to.redirectedFrom && !!helpOf(to) && helpOf(to) !== helpOf(from)
})
onBeforeUnmount(stopWatchingNavigation)

// A URL that names a place in the hub opens it. On a phone the hub covers the page, so leaving the page closes it: the page is what the dealer asked for, unless the link named a page in the hub, or Back and Forward return to a URL that does.
watch([() => route.path, helpSlug], ([path, slug], [previousPath]) => {
  if (isMobile.value && path !== previousPath) {
    globalStore.helpHubOpen = !!slug && (fromHistory || linkNamedPage)
    return
  }
  if (!slug || globalStore.helpHubOpen) return
  globalStore.helpHubOpen = true
  makeRoomForHelpHub()
})

// Back, forward and links move the tab too.
watch(helpTab, (value) => {
  tab.value = value
})

// Where each page on the trail was left scrolled to, keyed by slug ('' is the top level).
const scrollTops = new Map()
// Where the page that just opened scrolls to: its top, or where it was left on a return.
let openAt = 0

// The trail grows on a new page; a page already on it (the arrow, a "Back to" row) cuts it back to that page.
watch(helpPage, (page, previous) => {
  if (panel.value?.body) scrollTops.set(previous ?? '', panel.value.body.scrollTop)

  if (!page) {
    openAt = scrollTops.get('') ?? 0
    trail.value = []
    scrollTops.clear()
    return
  }

  const index = trail.value.lastIndexOf(page)
  if (index === -1) {
    openAt = 0
    // The list heads every path through the steps, so it never follows a page: opened afresh, it starts the trail over.
    const kept = page === HELP_HUB_GET_STARTED_SLUG ? [] : trail.value
    trail.value.slice(kept.length).forEach((cut) => scrollTops.delete(cut))
    trail.value = [...kept, page]
    return
  }

  openAt = scrollTops.get(page) ?? 0
  trail.value.slice(index + 1).forEach((cut) => scrollTops.delete(cut))
  trail.value = trail.value.slice(0, index + 1)
}, { immediate: true })

// A new page opens at its top; the arrow and a "Back to" row return to where that page was left.
watch(helpPage, () => panel.value?.body?.scrollTo(0, openAt), { flush: 'post' })

// A Chat list page opens at its top, and the tab returns to its own.
watch(chatPage, () => panel.value?.body?.scrollTo(0, 0), { flush: 'post' })

// How long this opening has lasted, for `ms_open`; null while the drawer is shut.
let openedAt = null

// A URL that already named a place in the hub is what opened it; otherwise the opener said so.
const openSource = () => (
  helpSlug.value && globalStore.helpHubSource === HELP_BAR_SOURCE.OTHER
    ? HELP_BAR_SOURCE.DEEP_LINK
    : globalStore.helpHubSource
)

const trackOpened = () => {
  openedAt = Date.now()
  safeCapture(POSTHOG_EVENTS.HELP_BAR_OPENED, { source: openSource(), slug: helpSlug.value ?? null })
}

// NOT `immediate`: the callback also closes the hub's URL state, and running that at mount
// would clear the slug the next opening is supposed to return to. A hub that starts open is
// reported below instead.
//
// A closed hub shows nothing, so the URL stops naming a place in it; the place is kept for the next opening.
watch(() => globalStore.helpHubOpen, (open) => {
  if (open) {
    trackOpened()
    return
  }

  safeCapture(POSTHOG_EVENTS.HELP_BAR_CLOSED, {
    slug: helpSlug.value ?? null,
    ms_open: openedAt ? Date.now() - openedAt : null,
  })
  openedAt = null
  closeHelp()
})

// The hub was already open before the watcher existed — a reload, or arriving on a URL that
// names a page in it.
if (globalStore.helpHubOpen) trackOpened()

// Every help page the dealer lands on, however they got there.
watch(helpPage, (page) => {
  if (!page) return
  safeCapture(POSTHOG_EVENTS.HELP_PAGE_VIEWED, {
    slug: page,
    source: fromHistory
      ? HELP_PAGE_SOURCE.HISTORY
      : (trail.value.length > 1 ? HELP_PAGE_SOURCE.IN_DRAWER : HELP_PAGE_SOURCE.DEEP_LINK),
  })
}, { immediate: true })

// Reset on layout unmount so the chat bubble and bars are not left shifted.
// The support socket belongs to the panel, not to the Chat tab: switching to Help must not
// drop a live conversation, so it is closed here, once, when the hub itself goes.
onBeforeUnmount(() => {
  globalStore.helpHubOpen = false
  document.documentElement.style.removeProperty('--help-hub-inset')
  supportStore.teardown()
})
</script>

<template>
  <AppSidePanel
    ref="panel"
    :id="HELP_HUB_PANEL_ID"
    :open="globalStore.helpHubOpen"
    :close-on-escape="closeOnEscape"
    title="Help Hub"
    :aria-label="pageHeader ? 'Help Hub' : undefined"
    :hide-close="pageHeader"
    v-model:width="panelWidth"
    :resizable="!isMobile"
    :min-width="HELP_HUB_WIDTH"
    :max-width="HELP_HUB_MAX_WIDTH"
    :inset-top="globalStore.appChromeHeight"
    :scroll-header="isMobile"
    @close="close"
  >
    <!-- A help page swaps the title for the way back up and fills both actions: a plain Back on the Get started list and on the Chat tab's list pages, which have only the top level above them; below the list, an arrow to the page before this one and the "Back to" menu, a row for each page opened on the way here. -->
    <template v-if="pageHeader" #header>
      <!-- -ml-2 puts the button on the title's edge, and it keeps its own width in the header's grid; Figma's 16px arrow has a 2px stroke, 3 on the icon's 24 grid. -->
      <AppButton
        v-if="isGetStartedList || chatPage"
        variant="tertiary"
        :coloured="false"
        class="-ml-2 justify-self-start"
        leading-icon="heroicons-outline:arrow-left"
        icon-class="text-gray-500 [&>path]:stroke-[3px]"
        @click="openHelp(chatPage ? HELP_HUB_TAB.CHAT : null)"
      >
        Back
      </AppButton>

      <!-- The same arrow on its own, 12px before the menu (Figma 27571:642788). -->
      <div v-else class="-ml-2 flex items-center gap-x-3">
        <AppButton
          variant="tertiary"
          :coloured="false"
          aria-label="Back"
          leading-icon="heroicons-outline:arrow-left"
          icon-class="text-gray-500 [&>path]:stroke-[3px]"
          @click="openHelp(previousPage)"
        />

        <AppDropdown placement="bottom-start" width="w-[290px]" flush>
          <template #trigger="{ open }">
            <AppButton variant="tertiary" :coloured="false">
              Back to
              <Icon
                icon="heroicons-solid:chevron-down"
                class="size-4 shrink-0 text-gray-500 transition-transform duration-200"
                :class="{ 'rotate-180': open }"
              />
            </AppButton>
          </template>

          <div class="py-2">
            <!-- The top level and the list head every path; the open page's row reads blue. -->
            <AppDropdownItem size="panel" item-class="!gap-2 font-medium" @select="openHelp(null)">
              <template #icon>
                <span class="shrink-0 rounded bg-blue-50 p-1">
                  <Icon icon="heroicons:question-mark-circle" class="block size-4 text-tint-1" />
                </span>
              </template>
              Help Hub
            </AppDropdownItem>

            <AppDropdownItem
              size="panel"
              icon="heroicons-outline:book-open"
              icon-class="size-4"
              item-class="!px-5"
              @select="openHelp(HELP_HUB_GET_STARTED_SLUG)"
            >
              Get started
            </AppDropdownItem>

            <AppDropdownItem
              v-for="page in trailPages"
              :key="page.slug"
              size="panel"
              :item-class="trailRowClass(page)"
              :aria-current="page.slug === helpPage ? 'page' : undefined"
              @select="openHelp(page.slug)"
            >
              {{ page.title }}
            </AppDropdownItem>
          </div>
        </AppDropdown>
      </div>

      <!-- -mr-1 puts the circle on the content's 16px edge. -->
      <AppButton
        variant="tertiary"
        :coloured="false"
        rounded="full"
        class="-mr-1 !size-8"
        aria-label="Close Help Hub"
        @click="close"
      >
        <Icon icon="heroicons-solid:x" class="size-4 shrink-0 text-gray-500" />
      </AppButton>
    </template>

    <!-- The top level; the two states keep its tabs and field, and take the content's place. -->
    <template v-if="chrome" #default>
      <!-- A column, so a state fills what the tabs leave and sits mid-height in it. -->
      <div class="flex min-h-full flex-col">
        <!-- Stays put while the tab's content scrolls under it; pb-3 keeps that content off the search field. On a phone it scrolls away with the header, leaving the content the height. -->
        <!-- Its top overlaps the header's bottom padding (the sticky offset matches), so the tabs sit 12px under the title as Figma draws them. -->
        <div class="-mt-3 bg-white pb-3" :class="{ 'sticky -top-3 z-10': !isMobile }">
          <AppTabToggle
            :model-value="tab"
            :options="HELP_HUB_TABS"
            variant="link"
            compact
            class="px-6"
            @update:model-value="selectTab"
          />

          <!-- The Help tab's search field, on the sections' 16px gutter rather than the header's 24 so it lines up with the rows under it; the Chat tab's composer is the footer (Figma 28708:73192). -->
          <div v-if="isHelpTab" class="px-4 pt-3">
            <AppInput
              v-model="search"
              size="md"
              leading-icon="heroicons-outline:search"
              placeholder="How can we help?"
              aria-label="Search help"
              clearable
            />
          </div>

          <!-- The thread's way back sits where the Help tab keeps its field (Figma 29855:72883), so it stays put while the conversation scrolls; it also stands while the URL's ticket is still being found, or was not. -ml-2 puts the arrow on the gutter. -->
          <div v-else-if="supportStore.inConversation || ticketNumber" class="px-4 pt-3">
            <AppButton
              variant="link"
              class="-ml-2"
              leading-icon="heroicons-outline:arrow-left"
              icon-class="[&>path]:stroke-[3px]"
              @click="closeThread"
            >
              Back
            </AppButton>
          </div>
        </div>

        <template v-if="isHelpTab">
          <!-- Words in the field: the matches take the tab, over the states and the sections alike (Figma 29871:320227). -->
          <HelpHubSearchResults v-if="searching" :query="search" @clear="search = ''" />

          <!-- Strapi failed: Retry asks again, and the steps bring the page or the sections back. -->
          <HelpHubState
            v-else-if="failed"
            tone="error"
            icon="heroicons:exclamation-circle-solid"
            title="Couldn't load data"
            description="Something went wrong while loading the content. Please try again."
            action-label="Retry"
            action-icon="heroicons-outline:refresh"
            @action="strapiStore.fetchGetStartedSteps()"
          />

          <!-- Strapi answered without this page. -->
          <HelpHubState
            v-else-if="missing"
            icon="heroicons-outline:search"
            title="Content not found"
            description="This content may have been moved or deleted."
            action-label="Back to help hub"
            action-icon="heroicons-outline:arrow-left"
            @action="openHelp(null)"
          />

          <template v-else>
            <!-- Stays once every step is done, so the dealer can go back to them; hidden only when Strapi has none. -->
            <HelpHubGetStarted v-if="loading || steps.length" />
            <HelpHubWhatsNew />
            <HelpHubExploreTopics />
            <HelpHubHelpCategories />
          </template>
        </template>

        <HelpHubChat v-else />
      </div>
    </template>

    <!-- Every conversation, ticket or question, under a field that filters them (Figma 29807:70192 / 70193 / 70194). -->
    <template v-else-if="chatPage" #default>
      <HelpHubChatList :page="chatPage" />
    </template>

    <template v-else-if="showsList" #default>
      <HelpHubGetStartedList />
    </template>

    <template v-else-if="step" #default>
      <HelpHubStepPage :step="guidePage ?? step" :number="stepIndex + 1" :numbered="!guidePage" :locked="step.locked" :next="nextLinks" />
    </template>

    <!-- A step page's shape until Strapi answers: progress, title, video, button, guide. -->
    <template v-else #default>
      <div role="status" class="flex flex-col gap-y-6 px-4 pt-3 pb-6">
        <span class="sr-only">Loading</span>

        <div class="flex flex-col gap-y-2">
          <AppSkeleton width="w-24" height="h-3" />
          <AppSkeleton height="h-2" rounded="rounded-full" />
        </div>

        <div class="flex flex-col gap-y-3">
          <div class="flex flex-col gap-y-2">
            <div class="flex items-center gap-x-3">
              <AppSkeleton width="w-6" height="h-6" rounded="rounded-full" class="flex-none" />
              <AppSkeleton width="w-2/3" height="h-6" />
            </div>

            <AppSkeleton height="h-3.5" />
            <AppSkeleton width="w-5/6" height="h-3.5" />
          </div>

          <AppSkeleton height="h-44" rounded="rounded-2xl" />
          <AppSkeleton height="h-[34px]" />
        </div>

        <div class="flex flex-col gap-y-4">
          <AppSkeleton width="w-40" height="h-4" />

          <div class="flex flex-col gap-y-3">
            <AppSkeleton v-for="row in 3" :key="row" height="h-14" rounded="rounded-lg" />
          </div>
        </div>
      </div>
    </template>

    <!-- The Help tab, its two states and the Get started list share one footer, and a step page swaps its button; the Chat tab, its thread and its list pages put the composer there (Figma 28708:73192 / 29855:72883). -->
    <template v-if="chrome || showsList || step || chatPage" #footer>
      <!-- A thread's way to a person, over the composer (Figma 29855:72883); once handed over, the AI's help meanwhile (Figma 30603:646799). Held while a reply is coming. A thread support doesn't have yet (the local "What's new?") has no one to hand to. -->
      <AppButton
        v-if="chrome && !isHelpTab && supportStore.activeId"
        variant="link"
        class="w-full"
        :leading-icon="supportStore.escalated ? 'heroicons-outline:sparkles' : 'heroicons-outline:user'"
        :loading="supportStore.escalating || supportStore.askingAi"
        :disabled="supportStore.sending || supportStore.awaitingReply || supportStore.messagesLoading"
        @click="supportStore.escalated ? supportStore.askAi() : supportStore.escalate()"
      >
        {{ supportStore.escalated ? 'Ask AI to help while you wait' : SUPPORT_ESCALATE_LABEL }}
      </AppButton>

      <HelpHubChatComposer
        v-if="chatPage || (chrome && !isHelpTab)"
        v-model="draft"
        :attachments="supportStore.attachments"
        :ready="supportStore.readyAttachmentIds.length > 0"
        :sending="supportStore.sending"
        :uploading="supportStore.uploading"
        @send="sendMessage"
        @attach="supportStore.attachFiles($event)"
        @remove="supportStore.removeAttachment($event)"
      />

      <template v-else>
        <!-- A guide step's page keeps only the next-step button. -->
        <AppButton
          v-if="!guidePage"
          as="a"
          :href="CDP_SUPPORT_URL"
          target="_blank"
          rel="noopener"
          variant="link"
          trailing-icon="heroicons-outline:external-link"
          @click="safeCapture(POSTHOG_EVENTS.SUPPORT_CENTER_CLICKED)"
        >
          View our Help Hub
        </AppButton>

        <AppButton v-if="!step" variant="default" size="lg" @click="selectTab(HELP_HUB_TAB.CHAT)">
          Contact Us
        </AppButton>

        <!-- Figma draws the arrow on the first step's page alone, 16px with a 1.5px stroke. -->
        <AppButton
          v-else-if="nextStep"
          as="router-link"
          :to="helpLink(nextStep.key)"
          variant="default"
          size="lg"
          :trailing-icon="stepIndex === 0 && !guidePage ? 'heroicons:arrow-up-right' : null"
          icon-class="[&>path]:stroke-[2.25px]"
        >
          <!-- A step, or one of its pages, words this as its frame does. -->
          <span class="min-w-0 truncate">{{ (guidePage ?? step).nextStepLabel ?? 'Jump to Next Step' }}: {{ step.nextStepTitle ?? nextStep.title }}</span>
        </AppButton>

        <!-- The last step of the track returns to the hub's top level. -->
        <AppButton v-else variant="default" size="lg" @click="openHelp(null)">All set</AppButton>
      </template>
    </template>
  </AppSidePanel>
</template>
